import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Video,
  Lightbulb,
  Phone,
  Target,
  Calendar,
  Gift,
  FileText,
  UsersRound,
  Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Action, Subject } from '../../contexts/AbilityContext';

export interface TourStep {
  id: string;
  route: string;
  title: string;
  description: string;
  highlights: string[];
  icon: LucideIcon;
  permission?: { action: Action; subject: Subject };
  vendorOnly?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'dashboard',
    route: '/dashboard',
    title: 'Dashboard',
    description:
      'Your command center. Get a quick overview of your advocacy program performance at a glance.',
    highlights: [
      'View key stats: promoters, reviews, testimonials',
      'Quick actions to add promoters & prospects',
      'Analytics charts and activity feed',
      'Switch between Overview, Activity & Analytics tabs',
    ],
    icon: LayoutDashboard,
  },
  {
    id: 'promoters',
    route: '/promoters',
    title: 'Promoters',
    description:
      'Manage your customer advocates. Track their journey from invitation to active promotion.',
    highlights: [
      'Switch between Ikigai, List & Kanban views',
      'Add individual promoters or import in bulk',
      'Import from CSV or HubSpot CRM',
      'Track promoter engagement and status',
    ],
    icon: Users,
  },
  {
    id: 'reviews',
    route: '/reviews',
    title: 'Reviews',
    description:
      'Collect and manage customer reviews. Publish the best ones to your public page.',
    highlights: [
      'View all reviews by status',
      'Publish or feature top reviews',
      'Respond to reviews directly',
      'Export reviews for external use',
    ],
    icon: MessageSquare,
  },
  {
    id: 'testimonials',
    route: '/testimonials',
    title: 'Testimonials',
    description:
      'Request and manage video testimonials from your promoters to showcase social proof.',
    highlights: [
      'Request video testimonials from promoters',
      'Review and approve submissions',
      'Publish and feature best testimonials',
      'Track testimonial request status',
    ],
    icon: Video,
  },
  {
    id: 'use-cases',
    route: '/use-cases',
    title: 'Use Cases',
    description:
      'Create compelling success stories from your promoters\' experiences with your product.',
    highlights: [
      'Request use cases from promoters',
      'AI-assisted use case generation',
      'Publish and feature success stories',
      'Share on your public page',
    ],
    icon: Lightbulb,
  },
  {
    id: '1to1-calls',
    route: '/1to1-calls',
    title: '1-to-1 Calls',
    description:
      'Manage reference call requests between your prospects and promoters.',
    highlights: [
      'View call submissions and requests',
      'Approve or reject call frequency',
      'Track call completion status',
      'Connect prospects with the right promoters',
    ],
    icon: Phone,
  },
  {
    id: 'prospects',
    route: '/prospects',
    title: 'Prospects',
    description:
      'Track potential customers through your advocacy funnel. Connect them with promoters.',
    highlights: [
      'Switch between Funnel and List views',
      'Add prospects individually',
      'Track prospect engagement stages',
      'Match prospects to relevant promoters',
    ],
    icon: Target,
  },
  {
    id: 'meetings',
    route: '/meetings',
    title: '1-to-1 Calendar',
    description:
      'Your meeting hub for scheduling and tracking all 1-to-1 reference calls.',
    highlights: [
      'View upcoming and past meetings',
      'Calendar and list views available',
      'Track meeting outcomes',
      'Manage meeting availability',
    ],
    icon: Calendar,
  },
  {
    id: 'rewards',
    route: '/rewards',
    title: 'Rewards',
    description:
      'Incentivize your promoters with rewards. Configure automatic and bonus rewards.',
    highlights: [
      'Configure reward rules and amounts',
      'Send bonus rewards to top promoters',
      'View reward analytics and spending',
      'Manage your reward balance',
    ],
    icon: Gift,
    permission: { action: 'create', subject: 'Reward' },
  },
  {
    id: 'my-page',
    route: '/my-page',
    title: 'My Page',
    description:
      'Manage your public advocacy page. Customize how your brand and testimonials appear.',
    highlights: [
      'Preview your public page',
      'Toggle between draft and published',
      'Customize page content and branding',
      'Share your page link with prospects',
    ],
    icon: FileText,
    permission: { action: 'update', subject: 'Page' },
  },
  {
    id: 'team-members',
    route: '/teams-sales',
    title: 'Team Members',
    description:
      'Invite and manage your team. Assign roles and permissions to collaborate effectively.',
    highlights: [
      'Invite new team members',
      'Assign roles (Sales, CS, Manager, etc.)',
      'Manage permissions and access',
      'View team activity and performance',
    ],
    icon: UsersRound,
    permission: { action: 'read', subject: 'TeamMember' },
  },
  {
    id: 'settings',
    route: '/settings',
    title: 'Settings',
    description:
      'Configure your account preferences, company details, and platform settings.',
    highlights: [
      'Update profile and company info',
      'Manage products and onboarding setup',
      'Configure email templates',
      'Customize use case & review questions',
    ],
    icon: Settings,
    vendorOnly: true,
  },
];
